import React, {
  FC,
  ReactElement,
  ReactNode,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  DownloadUniversal,
  EditableOutlined,
  EmergencyUniversal,
  UploadUniversal,
  UserGroupOutlined,
} from '@duxton-web/icons';
import {
  Alert,
  Button,
  Card,
  ConfigProvider,
  Flex,
  Form,
  Input,
  Modal,
  Select,
  Tag,
  Typography,
  Upload,
} from '@duxton-web/ui';
import { notification, Popconfirm } from 'antd';

import { FeedbackType, openInAppFeedback } from '../../common/inAppFeedback';
import Service, {
  FormatErrorMessage,
  Group,
  NextSchedule,
} from '../../lib/App';
import AsyncLoader from '../../lib/AsyncLoader';
import csvToArray from '../../utils/csvToArray';

import './GroupCreate.css';

import _ from 'lodash';

import { AccessControlWrapper } from '@/common/components';
import { NameSpace } from '@/common/SegmentSelector/SegmentSelector';
import { useUserRolesContext } from '@/contexts/UserRolesContext';
import { UserAction } from '@/utils/accessControl';

enum GroupCreateMode {
  FILE = 'file',
  SEGP = 'segp',
  S3 = 's3',
}

export enum GroupViewMode {
  CREATE = 'create',
  EDIT = 'edit',
}

type IGroupCreateProps = {
  mode?: GroupViewMode;
  onCancel?: () => void;
  item: Group | null;
};

type NextScheduleLoader = new () => AsyncLoader<NextSchedule>;
const NextScheduleLoader = AsyncLoader as NextScheduleLoader;

/** Gets the name of the name of the group. */
export function groupName(group: Group | null): string {
  let facet: string = group?.facet;

  // Mexuser -> meu, Mexstore -> mes
  if (group?.source === 'segp' && group?.facet === 'mex') {
    if (group?.name.startsWith(NameSpace.MEXUSER)) {
      facet = 'meu';
    } else if (group?.name.startsWith(NameSpace.MEXSTORE)) {
      facet = 'mes';
    }
  }

  // Mexuser -> meu, Mexstore -> mes
  return facet + '.' + (group?.source === 'segp' ? 'segp.' : '') + group?.name;
}

const GroupCreate: FC<IGroupCreateProps> = ({ item, onCancel }) => {
  const { permissionUtils } = useUserRolesContext();

  const hasCreateAccess = permissionUtils.canPerformAction(
    UserAction.CREATE_GROUP,
  );
  const hasEditAccess = permissionUtils.canPerformAction(UserAction.EDIT_GROUP);

  const [mode, setMode] = useState<GroupCreateMode>(
    item ? (item?.source as GroupCreateMode) : GroupCreateMode.FILE,
  );
  const [updating, setUpdating] = useState(false);
  const editMode = item ? true : false;
  const hasWriteAccess = editMode ? hasEditAccess : hasCreateAccess;
  const [submittable, setSubmittable] = useState<boolean>(false);
  const [showAppendConfirmation, setShowAppendConfirmation] =
    useState<boolean>(false);
  const [form] = Form.useForm();
  const currentItem = useRef<Group>(new Group(''));
  const fileContent = useRef<string>('');

  // Watch all values
  const values = Form.useWatch([], form);

  React.useEffect(() => {
    if (!hasWriteAccess) {
      setSubmittable(false);

      return;
    }

    form.validateFields({ validateOnly: true }).then(
      () => {
        setSubmittable(true);
      },
      (_e) => {
        setSubmittable(false);
      },
    );
  }, [form, hasWriteAccess, values]);

  // useeffect hook to force `item` prop to update state
  useEffect(() => {
    if (!_.isEqual(currentItem.current, item)) {
      if (item) currentItem.current = JSON.parse(JSON.stringify(item));
      else currentItem.current = null;

      const group = item || new Group('');

      if (!group.source) group.source = 'file';

      group['membersString'] = group?.members ? item.members.join(', ') : '';

      Object.keys(group).forEach((key: string) => {
        form.setFieldValue(key, group[key]);
      });
    }
  }, [item]);

  function getGroup(): Group {
    const group = new Group('');

    Object.keys(group).forEach((key: string) => {
      group[key] = form.getFieldValue(key);
    });

    group['members'] = csvToArray(form.getFieldValue('membersString'));

    return group;
  }

  function getMembersString(): string {
    return form.getFieldValue('membersString');
  }

  // createGroups will create groups
  const createGroup = async () => {
    setUpdating(true);

    // Prepare a name of the group. with the new name
    let groupCopy = getGroup();
    groupCopy.name = groupName(groupCopy);

    if (groupCopy?.facet === 'mex') {
      groupCopy.facet = groupCopy.name.substring(
        0,
        groupCopy.name.indexOf('.'),
      );
    }

    try {
      // Create the group
      await Service.planner!.createGroup(groupCopy);
      notification.success({
        message: 'Creating a Group',
        description: 'Group ' + groupCopy.name + ' was successfully created.',
      });
      openInAppFeedback({
        type: FeedbackType.NPS,
        context: 'groupPage',
        event: 'groupCreated',
      });
      onCancel!();
    } catch (e) {
      notification.error({
        message: 'Creating a Group',
        description: 'Group was not created, due to: ' + FormatErrorMessage(e),
      });
    } finally {
      setUpdating(false);
    }
  };

  const updateGroup = async () => {
    setUpdating(true);

    let groupCopy = getGroup();

    try {
      // Create the group
      await Service.planner!.updateGroup(groupCopy);
      notification.success({
        message: 'Updating a Group',
        description: 'Group ' + groupCopy.name + ' was successfully updated.',
      });
      onCancel!();
    } catch (e) {
      notification.error({
        message: 'Updating a Group',
        description: 'Group was not updated, due to: ' + FormatErrorMessage(e),
      });
    } finally {
      setUpdating(false);
    }
  };

  const getSupportedFacets = () => {
    return Service.cache!.getAllFacets();
  };

  const filterOption = (
    input: string,
    option?: { label: string; value: string; children },
  ) => {
    let label = option?.label;

    if (!label && typeof option.children === 'string') label = option?.children;

    return (label ?? '').toLowerCase().includes(input.toLowerCase());
  };

  function appendFileContent(append: boolean): void {
    setShowAppendConfirmation(false);

    if (!append) {
      form.setFieldValue('members', csvToArray(fileContent.current));
      form.setFieldValue('membersString', fileContent.current);

      return;
    }

    const membersString = getMembersString();
    const newMembersString = membersString + ',' + fileContent.current;

    form.setFieldValue('members', csvToArray(newMembersString));
    form.setFieldValue('membersString', newMembersString);
  }

  function fileSelected(file: any): boolean {
    const reader = new FileReader();
    reader.onload = () => {
      fileContent.current = reader.result!.toString();
      const membersString = getMembersString();

      if (membersString) {
        setShowAppendConfirmation(true);
      } else {
        form.setFieldValue('members', csvToArray(fileContent.current));
        form.setFieldValue('membersString', fileContent.current);
      }
    };
    reader.readAsText(file);

    // return false otherwise Upload component
    // will try to actually upload the file
    return false;
  }

  function isFileMode(): boolean {
    return GroupCreateMode.FILE === mode;
  }

  function isSegPMode(): boolean {
    return GroupCreateMode.SEGP === mode;
  }

  function getIDsCount(): number {
    const membersString = getMembersString();

    return csvToArray(membersString).length;
  }

  function renderSegPAlert(): ReactNode {
    if (!isSegPMode()) return null;

    return (
      <Alert
        showIcon
        icon={<EmergencyUniversal />}
        message="SegP Segments New Behaviour"
        type="warning"
        description={
          <Flex vertical gap={8}>
            You can now directly select SegP Segments from your Rollout Targets
            for easier and faster usage..
            <ol
              style={{
                marginLeft: 14,
              }}
            >
              <li>Select the variable you want to add a rollout target for</li>
              <li>Add rollout target</li>
              <li>
                In the constraint selection, select Passenger, Driver or
                Merchant
              </li>
              <li>Select SegP Segments from the operator dropdown</li>
              <li>Select one or multiple SegP Segments for your rollout</li>
              <li>Make sure to save your changes and requesting approval</li>
            </ol>
          </Flex>
        }
      />
    );
  }

  function handleDownloadCSV() {
    const group = getGroup();
    const membersString = getMembersString();
    const csv = 'data:text/csv;charset=utf-8,' + membersString;
    const excel = encodeURI(csv);
    const link = document.createElement('a');

    link.setAttribute('href', excel);
    link.setAttribute('download', `${group.name}.csv`);
    link.click();
  }

  function renderUploadDownloadSection(): ReactNode {
    if (editMode && form.getFieldValue('source') === GroupCreateMode.S3)
      return null;

    return (
      <Flex align="center" gap={8}>
        <Popconfirm
          title="Selecting 'Yes' will append to the existing data. Selecting 'No' will overwrite existing data. Please select an option?"
          placement="bottomLeft"
          okText="Yes"
          cancelText="No"
          open={showAppendConfirmation}
          onConfirm={() => appendFileContent(true)}
          onCancel={() => appendFileContent(false)}
        >
          <Upload beforeUpload={fileSelected} fileList={[]} accept=".csv, .txt">
            <Button
              icon={<UploadUniversal />}
              type="primary"
              size="small"
              disabled={!hasWriteAccess}
            >
              Upload CSV File
            </Button>
          </Upload>
        </Popconfirm>

        <Button
          size="small"
          icon={<DownloadUniversal />}
          type="primary"
          onClick={handleDownloadCSV}
        >
          Download
        </Button>
      </Flex>
    );
  }

  function renderIDsSection(): ReactNode {
    return (
      <Card size="small">
        <Form layout="vertical" form={form}>
          <Flex vertical gap={8}>
            {/* {renderExceedMaxCountLimitAlert()} */}
            <Form.Item
              label="IDs"
              name="membersString"
              required
              rules={[
                {
                  required: true,
                },
              ]}
            >
              <Input.TextArea
                disabled={
                  !hasWriteAccess ||
                  form.getFieldValue('source') === GroupCreateMode.S3
                }
                placeholder="Copy and paster your item, or type in directly. Separete your items with a comma (,)."
                rows={10}
              />
            </Form.Item>
            <Flex align="center" justify="space-between">
              <div>{renderUploadDownloadSection()}</div>
              {getIDsCount()}
            </Flex>
          </Flex>
        </Form>
      </Card>
    );
  }

  function renderGroupForm(): ReactNode {
    if (isSegPMode()) return false;

    return (
      <ConfigProvider
        theme={{
          components: {
            Form: {
              itemMarginBottom: 0,
            },
          },
        }}
      >
        <Flex vertical gap={16}>
          <Card size="small">
            <Flex vertical gap={8}>
              <Typography.Text strong>Main Details</Typography.Text>
              <Form layout="vertical" form={form}>
                <Flex vertical gap={8}>
                  <Form.Item
                    name="name"
                    label="Name"
                    validateFirst
                    rules={[
                      {
                        required: true,
                        message: 'Enter group name',
                      },
                      {
                        pattern: new RegExp(/^[a-zA-Z]/),
                        message: 'First character should be a letter',
                      },
                      {
                        pattern: new RegExp(/^[a-zA-Z][0-9a-zA-Z._-]*$/),
                        message:
                          'Only alphanumeric, dash and underscore are allowed',
                      },
                    ]}
                  >
                    <Input
                      disabled={editMode}
                      placeholder="The name of the group"
                    />
                  </Form.Item>

                  <Form.Item label="Group Data Type" name="facet" required>
                    <Select
                      style={{ width: '100%' }}
                      placeholder="Select group data type"
                      disabled={editMode}
                      showSearch={true}
                      filterOption={filterOption}
                      options={getSupportedFacets().map((facet) => {
                        return {
                          label: facet.title,
                          value: facet.name,
                        };
                      })}
                    />
                  </Form.Item>
                </Flex>
              </Form>
            </Flex>
          </Card>
          {renderIDsSection()}
        </Flex>
      </ConfigProvider>
    );
  }

  function renderGroupTypeToggle(): ReactNode {
    if (editMode) return false;

    return (
      <Flex align="center" justify="space-between" gap={8}>
        <Flex
          align="center"
          gap={8}
          className={`group-create-mode-container ${
            isFileMode() ? 'selected' : ''
          }`}
          onClick={() => {
            setMode(GroupCreateMode.FILE);
          }}
        >
          <Flex
            align="center"
            justify="center"
            className="group-create-mode-icon-container"
          >
            <EditableOutlined
              style={{ color: isFileMode() ? '#FFF' : '#000' }}
            />
          </Flex>

          <Typography.Text>Static</Typography.Text>
        </Flex>
        <Flex
          align="center"
          gap={8}
          className={`group-create-mode-container ${
            isSegPMode() ? 'selected' : ''
          }`}
          onClick={() => {
            setMode(GroupCreateMode.SEGP);
          }}
        >
          <Flex
            align="center"
            justify="center"
            className="group-create-mode-icon-container"
          >
            <UserGroupOutlined
              style={{ color: isSegPMode() ? '#FFF' : '#000' }}
            />
          </Flex>

          <Typography.Text>Import SegP</Typography.Text>
          <Tag color="orange">MOVED</Tag>
        </Flex>
      </Flex>
    );
  }

  function renderGroupCreateModal(): ReactElement {
    return (
      <Modal
        centered
        width={760}
        open={true}
        title={
          <Typography.Title level={3}>
            {editMode ? (
              <Flex align="center" gap={8}>
                Edit Group
                <AccessControlWrapper action={UserAction.EDIT_GROUP}>
                  {(hasAccess) => (
                    <Tag
                      color="red"
                      size="small"
                      style={{
                        fontWeight: 'normal',
                        display: hasAccess ? 'none' : 'block',
                      }}
                    >
                      no edit access
                    </Tag>
                  )}
                </AccessControlWrapper>
              </Flex>
            ) : (
              'Create Group'
            )}
          </Typography.Title>
        }
        cancelText="Cancel"
        cancelButtonProps={{
          size: 'small',
        }}
        onCancel={onCancel}
        okText={editMode ? 'Save' : 'Create Group'}
        okButtonProps={{
          type: 'primary',
          size: 'small',
          disabled: !hasWriteAccess || updating || !submittable || isSegPMode(),
        }}
        onOk={() => {
          if (editMode) {
            updateGroup();

            return;
          }

          createGroup();
        }}
      >
        <Flex vertical gap={16} id="group-create-container">
          {renderGroupTypeToggle()}
          {renderGroupForm()}
          {renderSegPAlert()}
        </Flex>
      </Modal>
    );
  }

  return renderGroupCreateModal();
};

export default GroupCreate;
